import {isFiletypeVideo} from '@Utils/video.util';
import {canvasToPngBlob, getClampedImageFileDimensions, imageFileToDataUrl, isFiletypeImage, PNG_MIME_TYPE, UserImageUploadErrorCode} from '@Utils/image.util';
import type {GridUserImageItemStorage} from '@Components/base-grid/components/grid-user-image-item';
import {getGridUserImageItemStorageForUserImageVO} from '@Components/base-grid/components/grid-user-image-item';
import type {GridUserVideoItemStorage} from '@Components/base-grid/components/grid-user-video-item';
import {getGridUserVideoItemStorageForUserVideoVO} from '@Components/base-grid/components/grid-user-video-item';
import type {GridUserAudioItemStorage} from '@Components/base-grid/components/grid-user-audio-item';
import {getGridUserAudioItemStorageForUserAudioVO} from '@Components/base-grid/components/grid-user-audio-item';
import type {AddMediaItem, AudioData, DeferredImageUploadData, ElementData, ImageData, TempImageData, VideoData} from '@Libraries/add-media-library';
import {
  ElementDataType,
  getAudioDataFromGridUserAudioItemStorage,
  getImageDataFromGridUserImageItemStorage,
  getVideoDataFromGridUserVideoItemStorage,
  isDeferredImageUploadData,
} from '@Libraries/add-media-library';
import {GROWL_TYPE, showMessageGrowl} from '@Components/message-growl';
import {assertNever} from '@Utils/assert-never.util';
import type {GridItemStorage} from '@Components/base-grid/components/grid-item';
import {GRID_ITEM_TYPE} from '@Components/base-grid/components/grid-item';
import {isGridUserImageItem} from '@Components/base-grid/base-grid.library';
import {getUserId, isUserLoggedIn} from '@Libraries/user.library';
import {GA4EventName, GA4EventParamName, trackPosterBuilderGA4Events, UploadsFolderGA4Type} from '@Libraries/ga-events';
import type {UploadsFolderGA4Source} from '@Libraries/ga-events';
import {MODAL_IDS, openModal} from '@Components/modal-container-deprecated/modal-container';
import {updateUnlinkedMediaState} from '@Panels/user-media-panel/user-media-panel-reducer';
import type {GridAiImageItemStorage, GridStockImageItemStorage} from '@Components/base-grid/components/grid-stock-image-item';
import type {AIGenerateImageBackendVO} from '@Libraries/pmw-stock-media-library';
import {v4 as uuidv4} from 'uuid';
import {ImageItemSource} from '@Libraries/image-item.library';
import {isHeicImageFile, isSvgFile} from '@Utils/file.util';
import {makeStringUnique} from '@Utils/string.util';
import chunk from 'lodash/chunk';
import type {UserVideoVOResponse} from './user-video-library';
import {filterValidVideoFiles, isVideoFileValid} from './user-video-library';
import type {UserImageVOResponse} from './user-image-library';
import {filterValidImageFiles, isGifFile, isImageFileValid, isMultiFrameGIF, UserImageSource} from './user-image-library';
import type {UserAudioVOResponse} from './user-audio-library';
import {filterValidAudioFiles, isAudioFileValid} from './user-audio-library';
import Emitter from '../services/event-emitter';
import {hideLoading, showLoading, updateLoadingProgress, updateLoadingText} from './loading-toast-library';
import {progressHandler} from './upload-library';
import {pollableJob} from './pollable-job.library';
import {warmStreamFastPath} from './polling-ajax.library';
import type {PollEnvelope, PollKickoffAck, PollTransportParams} from './pollable-job.library.types';
import {PollFailError, PollError, PollTimeoutError} from './pollable-job.library.types';

let fileUploadQueue: UploadingFilesData[] = [];
let uploading = false;
// Whether to add all uploaded files together (send all files together to the onUpload function)
let addInBulk = false;
let currentUploadFileIndex = 0;

export const UPLOAD_LOADING_KEY = 'uploadingUserMedia';

export const MULTI_MEDIA_SOURCE_GETTY = 'getty';
export const MULTI_MEDIA_SOURCE_UPLOAD = 'upload';
export const MULTI_MEDIA_SOURCE_DRAWING = 'drawing';
export const MULTI_MEDIA_SOURCE_EMAIL_UPLOAD = 'email_upload';
export const MULTI_MEDIA_UPLOAD_SOURCES = [MULTI_MEDIA_SOURCE_UPLOAD, MULTI_MEDIA_SOURCE_EMAIL_UPLOAD];

const DEFERRED_EDITOR_UPLOAD_IMAGE_MIME_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif']);

export const supportsDeferredEditorUpload = (file: File): boolean => {
  return DEFERRED_EDITOR_UPLOAD_IMAGE_MIME_TYPES.has(file.type);
};

export const MAX_GETTY_IMAGES_PER_EMAIL = 12;
export const MAX_GETTY_VIDEOS_PER_EMAIL = 3;
export const MAX_ALLOWED_FILE_SIZE = 1024 * 1024 * 1024; // 1 GB

export type UploadFileDataTypes = UserMediaVOResponse | TempImageData | DeferredImageUploadData | (UserMediaVOResponse | TempImageData | DeferredImageUploadData)[];

export const getSingleUploadedMediaItem = (data: UploadFileDataTypes): UserMediaVOResponse | TempImageData | null => {
  if (Array.isArray(data) || isDeferredImageUploadData(data)) {
    return null;
  }
  return data;
};

export enum MultiMediaUploadSource {
  UPLOAD = MULTI_MEDIA_SOURCE_UPLOAD,
  EMAIL_UPLOAD = MULTI_MEDIA_SOURCE_EMAIL_UPLOAD,
  /**
   * An upload made through these lands linked into the brand kit named by the brandId param.
   * Values mirror UserMultiMediaItemVO::MULTI_MEDIA_SOURCE_BRAND_LOGOS / _BRAND_IMAGES.
   */
  BRAND_LOGOS = 'brand-logos',
  BRAND_IMAGES = 'brand-images',
}

export enum UserMediaType {
  IMAGE = 'IMAGE',
  VIDEO = 'VIDEO',
  AUDIO = 'AUDIO',
  IMAGE_BACKGROUND = 'IMAGE_BACKGROUND',
  VIDEO_BACKGROUND = 'VIDEO_BACKGROUND',
  DRAWING = 'SVG',
}

/**
 * The video-upload FAIL code for a video too long or too large to process; mirrors the backend's
 * UserVideoUploadErrorCode::INPUT_LIMIT_EXCEEDED wire value.
 */
const VIDEO_UPLOAD_LIMITS_EXCEEDED_ERROR_CODE = 'upload_limits_exceeded';

interface UploadImagesOpts {
  collectionId: string;
  uploadSrc: MultiMediaUploadSource;
  addInBulk: boolean;
  uploadVectors: boolean;
  mirror?: boolean;
  isUploadedFromImageTabInEmailMaker?: boolean;
  shouldLoadDataUrlImage?: boolean;
}

interface UploadedFileValidity {
  validFiles: File[];
  invalidFiles: File[];
}

interface UploadVideosOpts {
  collectionId: string;
  uploadSrc: MultiMediaUploadSource;
  addInBulk: boolean;
  mirror?: boolean;
  onVideoLimitsExceeded?: () => void;
}

interface UploadingFilesData {
  type: UserMediaType;
  file: File;
  onUpload: (type: UserMediaType, data: UploadFileDataTypes, currentUploadIndex?: number, totalItemsToUpload?: number) => void;
  onAfterUpload: (userMediaType: UserMediaType, data: UserMediaVOResponse[] | UserMediaVOResponse) => void;
  onError?: (file: File, currentUploadIndex?: number, totalItemsToUpload?: number) => void;
  /** Runs when the server refuses the video as too long or too large to process, before onError. */
  onVideoLimitsExceeded?: () => void;
  source: string;
  collectionId: string;
  mirror?: boolean;
  shouldLoadDataUrlImage?: boolean;
  shouldDeferImageUploadToEditor?: boolean;
  tempUploadingImageUID?: string;
  /** The kit a brand-source upload files into; the active kit when unset. */
  brandId?: string;
}

interface UserCollectionsVOResponse {
  i: string;
  n: string;
  s: number;
  e: string;
  t: string;
}

interface UploadFileOpts {
  collectionId: string;
  uploadSrc: MultiMediaUploadSource;
  addInBulk: boolean;
  isUploadedFromImageTabInEmailMaker: boolean;
  shouldLoadDataUrlImage: boolean;
  shouldDeferImageUploadsToEditor: boolean;
  onAfterUpload?: (type: UserMediaType, data: UserMediaVOResponse[] | UserMediaVOResponse) => void;
  /** The kit a brand-source upload files into; the active kit when unset. */
  brandId?: string;
}

export interface FileUploadMethods {
  onFileUpload: (type: UserMediaType, data: UploadFileDataTypes, currentUploadIndex?: number, totalUploadsCount?: number) => void;
  onUploadError: (file: File, currentUploadIndex?: number, totalItemsToUpload?: number) => void;
  onAfterUpload?: (type: UserMediaType, data: UserMediaVOResponse[] | UserMediaVOResponse) => void;
  onVideoLimitsExceeded?: () => void;
}

export interface UserCollectionsStorage {
  name: string;
  id: string;
  type: string;
  isShared: boolean;
}

/**
 * Load and display all user collections
 * @param {string} collectionType
 * @returns {Promise<Array>}
 * @throws Exception
 */
export const getUserCollections = async (collectionType: UserMediaType): Promise<UserCollectionsStorage[]> => {
  const data = (await window.PMW.readLocal('collection/getUserCollections', {
    collectionType,
  })) as UserCollectionsVOResponse[];

  const collections = [];
  for (const collectionData of data) {
    collections.push({
      id: collectionData.i,
      name: collectionData.n,
      type: collectionType,
      isShared: collectionData.s !== 0,
    });
  }
  return collections;
};

export const DEFAULT_NEW_FOLDER_NAME = 'Untitled';

const ROOT_USER_COLLECTION_ID = '';
/** Twin of User::MAX_ITEMS_MARKED_USED_PER_REQUEST: the server rejects a larger `hashedIds` batch outright. */
const MAX_ITEMS_MARKED_USED_PER_REQUEST = 100;

/** "Untitled", or "Untitled (n)" when a folder already carries that name. */
export const getUniqueNewFolderName = (existingFolderNames: string[]): string => {
  const existingFolderNamesMap = Object.fromEntries(
    existingFolderNames.map((folderName) => {
      return [folderName, true];
    })
  );
  return makeStringUnique(DEFAULT_NEW_FOLDER_NAME, existingFolderNamesMap);
};

const DEFAULT_NEW_FOLDER_NAME_PATTERN = new RegExp(`^${DEFAULT_NEW_FOLDER_NAME}( \\(\\d+\\))?$`);

/** Whether a folder name is one getUniqueNewFolderName hands out, so the user typed no name of their own. */
export const isDefaultNewFolderName = (folderName: string): boolean => {
  return DEFAULT_NEW_FOLDER_NAME_PATTERN.test(folderName);
};

/** Reports a newly saved folder: where the user made it, and whether they typed a name or kept the starting one. */
export const trackUploadsFolderCreated = (source: UploadsFolderGA4Source, folderName: string): void => {
  trackPosterBuilderGA4Events(GA4EventName.UPLOADS_MODAL_CREATE_FOLDER, {
    [GA4EventParamName.SOURCE]: source,
    [GA4EventParamName.TYPE]: isDefaultNewFolderName(folderName) ? UploadsFolderGA4Type.UNTITLED : UploadsFolderGA4Type.TITLED,
  });
};

/**
 * The "My Photos / My Videos / My Audios" pseudo-folder standing for the uploads root; moving into it takes items out of every folder.
 */
export const getRootUserCollection = (multimediaType: UserMediaType): UserCollectionsStorage => {
  return {
    id: ROOT_USER_COLLECTION_ID,
    name: getTextForMyMultiMedia(multimediaType),
    type: multimediaType,
    isShared: false,
  };
};

/** The name of the folder with `collectionId` among `collections`; empty when it is not listed. */
export const getUserCollectionName = (collections: UserCollectionsStorage[], collectionId: string): string => {
  const collection = collections.find((listedCollection) => {
    return listedCollection.id === collectionId;
  });
  return collection?.name ?? '';
};

export interface SaveUserCollectionParams {
  name: string;
  collectionType: UserMediaType;
  collectionId?: string;
}

/**
 * Creates a folder when no collectionId is given, renames it otherwise, and returns the folder's id.
 */
export const saveUserCollection = async ({name, collectionType, collectionId = ''}: SaveUserCollectionParams): Promise<string> => {
  return (await window.PMW.writeLocal('collection/save', {
    name,
    hash: collectionId,
    collectionType,
  })) as string;
};

/**
 * @param {string} contributorID
 * @param {string} collectionID
 * @param {string} collectionType
 * @return {Promise<*>}
 */
export const removeContributor = (contributorID: string, collectionID: string, collectionType: UserMediaType): void => {
  void window.PMW.writeLocal('collection/removeContributorToCollection', {
    contributorId: contributorID,
    collectionId: collectionID,
    collectionType,
  });
};

/**
 * Returns the contributors to a collection
 * @param {string} collectionID
 * @param {string} collectionType
 * @return {Promise<*>}
 */
export const getContributorsToCollection = async (collectionID: string, collectionType: UserMediaType): Promise<UserCollectionsVOResponse[]> => {
  return (await window.PMW.readLocal('collection/getContributorsToCollection', {
    collectionId: collectionID,
    collectionType,
  })) as UserCollectionsVOResponse[];
};

const filterValidFilesWithinAllowedUploadLimit = async (files: File[], onUploadError?: (file: File) => void): Promise<File[]> => {
  const {validFiles, invalidFiles} = await getUploadedFilesValidity(files, onUploadError);

  const validFilesWithinAllowedUploadLimit: File[] = [];
  const validFilesExceedingAllowedUploadLimit: File[] = [];

  for (const validFile of validFiles) {
    if (validFile.size <= MAX_ALLOWED_FILE_SIZE) {
      validFilesWithinAllowedUploadLimit.push(validFile);
    } else {
      validFilesExceedingAllowedUploadLimit.push(validFile);

      if (onUploadError) {
        onUploadError(validFile);
      }
    }
  }

  showErrorMessageForInvalidOrSizeExceededFiles(invalidFiles, validFilesExceedingAllowedUploadLimit);

  return validFilesWithinAllowedUploadLimit;
};

export const removeUserItems = async (itemIds: string[], multimediaType: UserMediaType): Promise<void> => {
  await window.PMW.writeLocal('user/removeUserMultiMediaItems', {
    hashedIds: itemIds,
    multiMediaType: multimediaType,
  });
};

/**
 * Best-effort recency bookkeeping for the uploads modal's "Recently used" sort: stamps the added
 * items as just used in a design, and (server-side) bumps the folders containing them.
 * Fire-and-forget — a failed stamp must never surface to or block the add flow.
 */
export const markUserMediaItemsUsed = (addedItems: AddMediaItem[]): void => {
  if (!isUserLoggedIn()) {
    return;
  }
  getUploadedItemHashedIdsByMediaType(addedItems).forEach((hashedIds, mediaType) => {
    chunk(hashedIds, MAX_ITEMS_MARKED_USED_PER_REQUEST).forEach((hashedIdsChunk) => {
      void window.PMW.writeLocal('user/markUserMultiMediaItemsUsed', {
        hashedIds: hashedIdsChunk,
        multiMediaType: mediaType,
      }).catch((error: unknown) => {
        console.error(error);
      });
    });
  });
};

/**
 * The hashed filenames of the added items that exist as the user's uploads on the server, grouped by media type.
 * Items with no upload row yet (temp data-URL images, deferred editor uploads) have no hashed filename and are left out.
 */
const getUploadedItemHashedIdsByMediaType = (addedItems: AddMediaItem[]): Map<UserMediaType, string[]> => {
  const hashedIdsByMediaType = new Map<UserMediaType, string[]>();
  const addUploadedItemHashedId = (mediaType: UserMediaType, hashedId: string): void => {
    if (!hashedId) {
      return;
    }
    hashedIdsByMediaType.set(mediaType, [...(hashedIdsByMediaType.get(mediaType) ?? []), hashedId]);
  };
  for (const addedItem of addedItems) {
    if (addedItem.type === ElementDataType.IMAGE && 'hashedFilename' in addedItem) {
      addUploadedItemHashedId(UserMediaType.IMAGE, addedItem.hashedFilename);
    } else if (addedItem.type === ElementDataType.VIDEO) {
      addUploadedItemHashedId(UserMediaType.VIDEO, addedItem.hashedFilename);
    } else if (addedItem.type === ElementDataType.AUDIO) {
      addUploadedItemHashedId(UserMediaType.AUDIO, addedItem.filename);
    }
  }
  return hashedIdsByMediaType;
};

const getUploadedFilesValidity = async (files: File[], onUploadError?: (file: File) => void): Promise<UploadedFileValidity> => {
  const validFiles = [];
  const invalidFiles = [];
  for (const file of files) {
    // eslint-disable-next-line no-await-in-loop
    if (isImageFileValid(file) || isAudioFileValid(file) || (await isVideoFileValid(file))) {
      validFiles.push(file);
    } else {
      invalidFiles.push(file);

      if (onUploadError) {
        onUploadError(file);
      }
    }
  }
  return {
    validFiles,
    invalidFiles,
  };
};

/**
 * upload media files
 */
export const uploadFiles = async (fileUploadMethods: FileUploadMethods, files: File[], options: Partial<UploadFileOpts>): Promise<void> => {
  const opts: UploadFileOpts = {
    collectionId: '',
    uploadSrc: MultiMediaUploadSource.UPLOAD,
    addInBulk: false,
    isUploadedFromImageTabInEmailMaker: false,
    shouldLoadDataUrlImage: false,
    shouldDeferImageUploadsToEditor: false,
    ...options,
  };
  const filteredFiles = await filterValidFilesWithinAllowedUploadLimit(files, fileUploadMethods.onUploadError);
  const shouldDeferImageUploadToEditor = opts.shouldDeferImageUploadsToEditor && opts.collectionId === '' && filteredFiles.every(supportsDeferredEditorUpload);
  addInBulk = opts.addInBulk;
  await updateFileUploadQueue(filteredFiles, fileUploadMethods.onFileUpload, fileUploadMethods.onUploadError, {
    collectionId: opts.collectionId,
    source: opts.uploadSrc,
    isUploadedFromImageTabInEmailMaker: opts.isUploadedFromImageTabInEmailMaker,
    shouldLoadDataUrlImage: opts.shouldLoadDataUrlImage,
    shouldDeferImageUploadToEditor,
    onAfterUpload: fileUploadMethods.onAfterUpload,
    brandId: opts.brandId,
    onVideoLimitsExceeded: fileUploadMethods.onVideoLimitsExceeded,
  });
  await startUpload();
};

/**
 * Filter and upload image files
 */
export const uploadImages = async (
  files: File[],
  fileUploadMethods: FileUploadMethods,
  opts: UploadImagesOpts = {
    collectionId: '',
    uploadSrc: MultiMediaUploadSource.UPLOAD,
    addInBulk: false,
    uploadVectors: true,
    mirror: false,
    isUploadedFromImageTabInEmailMaker: false,
    shouldLoadDataUrlImage: false,
  }
): Promise<void> => {
  const filteredFiles = filterValidImageFiles(files, opts.uploadVectors, fileUploadMethods.onUploadError);
  addInBulk = opts.addInBulk;
  await updateFileUploadQueue(
    filteredFiles,
    fileUploadMethods.onFileUpload,
    fileUploadMethods.onUploadError,
    {
      collectionId: opts.collectionId,
      source: opts.uploadSrc,
      mirror: opts.mirror,
      isUploadedFromImageTabInEmailMaker: opts.isUploadedFromImageTabInEmailMaker,
      shouldLoadDataUrlImage: opts.shouldLoadDataUrlImage,
      onAfterUpload: fileUploadMethods.onAfterUpload,
    },
    UserMediaType.IMAGE
  );
  await startUpload();
};

/**
 * Filter and upload image background files
 */
export const uploadBackground = async (
  files: File[],
  fileUploadMethods: FileUploadMethods,
  opts = {collectionId: '', addInBulk: false, shouldLoadDataUrlImage: false}
): Promise<void> => {
  const filteredFiles = filterValidImageFiles(files, false, fileUploadMethods.onUploadError);
  await updateFileUploadQueue(
    filteredFiles,
    fileUploadMethods.onFileUpload,
    fileUploadMethods.onUploadError,
    {collectionId: opts.collectionId, shouldLoadDataUrlImage: opts.shouldLoadDataUrlImage, onAfterUpload: fileUploadMethods.onAfterUpload},
    UserMediaType.IMAGE_BACKGROUND
  );
  await startUpload();
};

/**
 * Filter and upload video files
 */
export const uploadVideos = async (
  files: File[],
  onFileUpload: (type: UserMediaType, data: UploadFileDataTypes, currentUploadIndex?: number, totalUploadsCount?: number) => void,
  onUploadError: (file: File, currentUploadIndex?: number, totalItemsToUpload?: number) => void,
  opts: UploadVideosOpts = {collectionId: '', uploadSrc: MultiMediaUploadSource.UPLOAD, addInBulk: false, mirror: false}
): Promise<void> => {
  const filteredFiles = await filterValidVideoFiles(files, onUploadError);
  addInBulk = opts.addInBulk;
  await updateFileUploadQueue(
    filteredFiles,
    onFileUpload,
    onUploadError,
    {collectionId: opts.collectionId, source: opts.uploadSrc, mirror: opts.mirror, onVideoLimitsExceeded: opts.onVideoLimitsExceeded},
    UserMediaType.VIDEO
  );
  await startUpload();
};
/**
 * Filter and upload audio files
 */
export const uploadAudios = async (
  files: File[],
  onFileUpload: (type: UserMediaType, data: UserMediaVOResponse[] | UserMediaVOResponse, currentUploadIndex?: number, totalUploadsCount?: number) => void,
  onUploadError: (file: File, currentUploadIndex?: number, totalItemsToUpload?: number) => void,
  opts = {collectionId: '', uploadSrc: MultiMediaUploadSource.UPLOAD}
): Promise<void> => {
  const filteredFiles = filterValidAudioFiles(files, onUploadError);
  await updateFileUploadQueue(
    filteredFiles,
    onFileUpload as (type: UserMediaType, data: UploadFileDataTypes) => void,
    onUploadError,
    {collectionId: opts.collectionId, source: opts.uploadSrc},
    UserMediaType.AUDIO
  );
  await startUpload();
};

const getFileType = async (file: File, uploadSource?: MultiMediaUploadSource, isUploadedFromImageTabInEmailMaker = false): Promise<UserMediaType> => {
  if (isGifFile(file)) {
    if (await isMultiFrameGIF(file)) {
      if (uploadSource === MultiMediaUploadSource.EMAIL_UPLOAD && isUploadedFromImageTabInEmailMaker) {
        return UserMediaType.IMAGE;
      }
      return UserMediaType.VIDEO;
    }
    return UserMediaType.IMAGE;
  }
  if (isFiletypeVideo(file.type)) {
    return UserMediaType.VIDEO;
  }
  if (isFiletypeImage(file.type)) {
    return UserMediaType.IMAGE;
  }
  if (isAudioFileValid(file)) {
    return UserMediaType.AUDIO;
  }
  throw new Error(`Invalid file type: ${file.type} , Media type: ${file.type}`);
};

/**
 * Updates the file upload queue with new files
 * @param {Array} filteredFiles
 * @param {string} onFileUpload callback to one each file uploads
 * @param opts
 * @param type
 * @param onUploadError
 */
const updateFileUploadQueue = async (
  filteredFiles: File[],
  onFileUpload: (type: UserMediaType, data: UploadFileDataTypes, currentUploadIndex?: number, totalUploadsCount?: number) => void,
  onUploadError: (file: File, currentUploadIndex?: number, totalItemsToUpload?: number) => void,
  opts: {
    collectionId: string;
    source?: MultiMediaUploadSource;
    mirror?: boolean;
    isUploadedFromImageTabInEmailMaker?: boolean;
    shouldLoadDataUrlImage?: boolean;
    shouldDeferImageUploadToEditor?: boolean;
    onAfterUpload?: (type: UserMediaType, data: UserMediaVOResponse[] | UserMediaVOResponse) => void;
    brandId?: string;
    onVideoLimitsExceeded?: () => void;
  },
  type?: UserMediaType
): Promise<void> => {
  if (filteredFiles.length > 0) {
    const queueData = [];
    for (const file of filteredFiles) {
      const fileType = type ?? (await getFileType(file, opts.source, opts.isUploadedFromImageTabInEmailMaker));
      queueData.push({
        type: fileType,
        onUpload: onFileUpload,
        onAfterUpload: opts.onAfterUpload ? opts.onAfterUpload : (_userMediaType: UserMediaType, _data: UserMediaVOResponse[] | UserMediaVOResponse): void => {},
        onError: onUploadError,
        onVideoLimitsExceeded: opts.onVideoLimitsExceeded,
        collectionId: opts.collectionId,
        file,
        source: opts.source ?? '',
        mirror: opts.mirror !== undefined && opts.mirror,
        shouldLoadDataUrlImage: opts.shouldLoadDataUrlImage,
        shouldDeferImageUploadToEditor: opts.shouldDeferImageUploadToEditor,
        brandId: opts.brandId,
      });
      Emitter.emit('userMedia:upload:start', {type: fileType, file, collectionId: opts.collectionId, mirror: opts.mirror});
    }

    fileUploadQueue.push(...queueData);
    updateLoadingText(UPLOAD_LOADING_KEY, getLoadingText());
  }
};

const HTTP_STATUS_PAYLOAD_TOO_LARGE = 413;

const getErrorMessage = (e: unknown): string | undefined => {
  return typeof e === 'object' && e !== null && 'message' in e && typeof e.message === 'string' ? e.message : undefined;
};

const getErrorStatus = (e: unknown): number | undefined => {
  return typeof e === 'object' && e !== null && 'status' in e && typeof e.status === 'number' ? e.status : undefined;
};

/**
 * Routes a media-upload failure to the user: fires the caller's onError, emits the fail
 * event, and shows a danger growl. A 413 with no message means the file exceeded php's
 * post_max_size (the server's JSON error body never reaches the client in that case), so
 * it gets the large-file message instead of the generic one.
 */
export const handleFileUploadError = (e: unknown, uploadData: UploadingFilesData, currentUploadIndex: number, totalItemsToUpload: number): void => {
  const message = getErrorMessage(e);
  let errorMessageText = message !== undefined && message.length > 0 ? message : window.i18next.t('pmwjs_upload_error');

  if (message === undefined && getErrorStatus(e) === HTTP_STATUS_PAYLOAD_TOO_LARGE) {
    errorMessageText = window.i18next.t('pmwjs_upload_generic_error_large_file');
  }

  if (isVideoUploadLimitsExceededError(e)) {
    uploadData.onVideoLimitsExceeded?.();
  }

  if (uploadData.onError) {
    uploadData.onError(uploadData.file, currentUploadIndex, totalItemsToUpload);
  }

  Emitter.emit('userMedia:upload:fail', {type: uploadData.type, file: uploadData.file, collectionId: uploadData.collectionId});
  showMessageGrowl({
    key: uploadData.tempUploadingImageUID,
    text: errorMessageText,
    type: GROWL_TYPE.DANGER,
  });
};

/**
 * Starts uploading the files in the queue and makes sure only one instants of this function is running at a time
 * @return {Promise<void>}
 */
const startUpload = async (): Promise<void> => {
  if (!onStartUploads()) {
    return;
  }
  const filesDataToUploadInBulk: (UserMediaVOResponse | TempImageData | DeferredImageUploadData)[] = [];

  for (let i = 0; i < fileUploadQueue.length; i++) {
    const uploadData = fileUploadQueue[i];

    currentUploadFileIndex = i;
    updateLoadingText(UPLOAD_LOADING_KEY, getLoadingText());
    updateLoadingProgress(UPLOAD_LOADING_KEY, 0);

    try {
      const fd = new FormData();
      fd.append('Filedata', uploadData.file);
      fd.append('type', uploadData.type);
      fd.append('mirror', uploadData.mirror !== undefined && uploadData.mirror ? 'true' : 'false');
      if (uploadData.brandId) {
        fd.append('brandId', uploadData.brandId);
      }
      let data: UploadFileDataTypes;
      if (uploadData.shouldDeferImageUploadToEditor && uploadData.type === UserMediaType.IMAGE && supportsDeferredEditorUpload(uploadData.file)) {
        // eslint-disable-next-line no-await-in-loop
        data = await getDeferredImageUploadData(uploadData);
      } else if (
        (uploadData.type === UserMediaType.IMAGE || uploadData.type === UserMediaType.IMAGE_BACKGROUND) &&
        uploadData.shouldLoadDataUrlImage &&
        !isSvgFile(uploadData.file) &&
        !isHeicImageFile(uploadData.file)
      ) {
        // eslint-disable-next-line no-await-in-loop
        const dataurl = await imageFileToDataUrl(uploadData.file);
        if (dataurl) {
          const tempUploadingImageUID = uuidv4();
          fd.append('tempUploadingImageUID', tempUploadingImageUID);

          data = {
            uid: tempUploadingImageUID,
            dataUrl: dataurl,
            type: ElementDataType.IMAGE,
            source: ImageItemSource.UPLOAD,
          };

          const currentUploadIndex = i + 1;
          const totalUploadsCount = fileUploadQueue.length;
          void uploadFile(fd, UPLOAD_LOADING_KEY, uploadData.type, uploadData.source)
            .then((response: UserMediaVOResponse) => {
              void onFileUploaded(response, uploadData).then(() => {
                uploadData.onAfterUpload(uploadData.type, response);
              });
            })
            .catch((e: unknown) => {
              handleFileUploadError(e, {...uploadData, tempUploadingImageUID}, currentUploadIndex, totalUploadsCount);
            });
        } else {
          // eslint-disable-next-line no-await-in-loop
          data = (await uploadFile(fd, UPLOAD_LOADING_KEY, uploadData.type, uploadData.source)) as UserMediaVOResponse;
          // eslint-disable-next-line no-await-in-loop
          await onFileUploaded(data, uploadData);
        }
      } else {
        // eslint-disable-next-line no-await-in-loop
        data = (await uploadFile(fd, UPLOAD_LOADING_KEY, uploadData.type, uploadData.source)) as UserMediaVOResponse;
        // eslint-disable-next-line no-await-in-loop
        await onFileUploaded(data, uploadData);
      }
      if (addInBulk) {
        filesDataToUploadInBulk.push(data);
      } else {
        uploadData.onUpload(uploadData.type, data, i + 1, fileUploadQueue.length);
      }
    } catch (e) {
      handleFileUploadError(e, uploadData, i + 1, fileUploadQueue.length);
    }
  }
  if (filesDataToUploadInBulk.length) {
    fileUploadQueue[0].onUpload(fileUploadQueue[0].type, filesDataToUploadInBulk);
  }

  onEndUploads();
};

/**
 * Move item to collection if collection is not null
 * @param {string} type one of the MEDIA_TYPE_* constants
 * @param {string} itemHashId
 * @param {string|null} collectionId
 */
const moveItemToCollection = async (type: UserMediaType, itemHashId: string, collectionId: string): Promise<void> => {
  if (collectionId) {
    await window.PMW.writeLocal('collection/moveUserItems', {
      hashedIds: [itemHashId],
      ch: collectionId,
      collectionType: type,
    });
  }
};

/** The event a successful move into a folder reports: drag-and-drop has its own, every other move surface counts as the Move popup. */
export type UploadsMoveGA4EventName = GA4EventName.UPLOADS_MODAL_MOVE_TO_FOLDER | GA4EventName.UPLOADS_MODAL_DRAG_TO_FOLDER;

export interface MoveUserItemsToCollectionParams {
  multimediaType: UserMediaType;
  selectedItems: GridItemStorage[];
  currentCollectionId: string;
  targetCollectionId: string;
  moveEventName: UploadsMoveGA4EventName;
}

/** What a move surface (drag-and-drop, the Move popup, the phone Move panel) reports once the server has answered. */
export interface ItemsMovedToFolder {
  movedItemIds: string[];
  folderName: string;
  mediaType: UserMediaType;
}

interface MoveToFolderTranslationKeys {
  movedSingular: string;
  movedPlural: string;
  failedMove: string;
}

const getMoveToFolderTranslationKeys = (mediaType: UserMediaType): MoveToFolderTranslationKeys => {
  switch (mediaType) {
    case UserMediaType.IMAGE:
    case UserMediaType.IMAGE_BACKGROUND:
    case UserMediaType.DRAWING:
      return {movedSingular: 'pmwjs_moved_image_to_folder', movedPlural: 'pmwjs_moved_image_to_folder_plural', failedMove: 'pmwjs_failed_move_image'};
    case UserMediaType.VIDEO:
    case UserMediaType.VIDEO_BACKGROUND:
      return {movedSingular: 'pmwjs_moved_video_to_folder', movedPlural: 'pmwjs_moved_video_to_folder_plural', failedMove: 'pmwjs_failed_move_video'};
    case UserMediaType.AUDIO:
      return {movedSingular: 'pmwjs_moved_audio_to_folder', movedPlural: 'pmwjs_moved_audio_to_folder_plural', failedMove: 'pmwjs_failed_move_audio'};
    default:
      return assertNever(mediaType);
  }
};

/** "2 photos moved to <strong>New year eve</strong>". The folder name is the user's own text, so i18next HTML-escapes it. */
const getMovedToFolderMessage = ({movedItemIds, folderName, mediaType}: ItemsMovedToFolder): string => {
  const translationKeys = getMoveToFolderTranslationKeys(mediaType);
  return window.i18next.t(movedItemIds.length === 1 ? translationKeys.movedSingular : translationKeys.movedPlural, {
    itemCount: movedItemIds.length,
    folderName,
    interpolation: {escapeValue: true},
  });
};

/** Confirms a move into a folder with the standard message growl; the uploads panel shows it at its own top edge. */
export const showMovedToFolderGrowl = (itemsMovedToFolder: ItemsMovedToFolder): void => {
  showMessageGrowl({
    text: getMovedToFolderMessage(itemsMovedToFolder),
    type: GROWL_TYPE.NEUTRAL,
    icon: 'icon-folder-shared',
    dangerouslySetInnerHTML: true,
    showCloseIcon: true,
  });
};

/**
 * Moves the selected media items the user owns into the target collection and returns the moved item ids.
 * Shows growls for items that cannot be moved (shared items the user does not own) and for partial failures;
 * throws if the move endpoint itself fails. Records the GA4 move-to-folder event when items land in a real
 * folder rather than the uploads root.
 */
export const moveUserItemsToCollection = async ({
  multimediaType,
  selectedItems,
  currentCollectionId,
  targetCollectionId,
  moveEventName,
}: MoveUserItemsToCollectionParams): Promise<string[]> => {
  const userId = getUserId();
  const itemHashedIdsToMove: string[] = [];
  let inaccessibleItemsCount = 0;
  for (const item of selectedItems) {
    const isNotOwnedByUser = !!currentCollectionId && userId !== item.uploaderId;
    if (isNotOwnedByUser) {
      inaccessibleItemsCount += 1;
      continue;
    }
    const hashedFilename: unknown = item.hashedFilename;
    if (typeof hashedFilename === 'string') {
      itemHashedIdsToMove.push(hashedFilename);
    }
  }
  if (inaccessibleItemsCount) {
    showMessageGrowl({text: getMessageForInvalidMediaMove(multimediaType)});
  }
  if (!itemHashedIdsToMove.length) {
    return [];
  }
  const movedItemIds = (await window.PMW.writeLocal('collection/moveUserItems', {
    hashedIds: itemHashedIdsToMove,
    ch: targetCollectionId,
    collectionType: multimediaType,
  })) as string[];
  if (!inaccessibleItemsCount && movedItemIds.length !== selectedItems.length) {
    showMessageGrowl({text: window.i18next.t(getMoveToFolderTranslationKeys(multimediaType).failedMove)});
  }
  if (movedItemIds.length && targetCollectionId !== ROOT_USER_COLLECTION_ID) {
    trackPosterBuilderGA4Events(moveEventName);
  }
  return movedItemIds;
};

const getLoadingText = (): string => {
  return `${window.i18next.t('pmwjs_uploading')} (${currentUploadFileIndex + 1} of ${fileUploadQueue.length})`;
};

const onStartUploads = (): boolean => {
  if (uploading) {
    return false;
  }
  uploading = true;
  currentUploadFileIndex = 0;
  showLoading(UPLOAD_LOADING_KEY, {
    text: getLoadingText(),
    priority: 10,
    progress: 0,
  });
  return true;
};

const onEndUploads = (): void => {
  uploading = false;
  fileUploadQueue = [];
  hideLoading(UPLOAD_LOADING_KEY);
};

const USER_MEDIA_UPLOAD_POLL_TIMEOUT_MS = 1_800_000; // matches legacy LONG_AJAX_CALL_TIMEOUT for all media types; video is the long pole

type UploadStatusChecker<T> = (ack: PollKickoffAck, transportParams: PollTransportParams) => Promise<PollEnvelope<T>>;

/**
 * Builds a V2 status checker bound to one status endpoint. Every media type's checker is the same
 * 4-liner (forward pollingKey + transport params to its status uri), differing only by uri and
 * resolved payload type, so they share this factory.
 */
export const makeUploadStatusChecker = <T>(statusUri: string): UploadStatusChecker<T> => {
  return async (ack: PollKickoffAck, transportParams: PollTransportParams): Promise<PollEnvelope<T>> => {
    return (await window.PMW.readLocal(statusUri, {
      pollingKey: ack.pollingKey,
      ...transportParams,
    })) as PollEnvelope<T>;
  };
};

const checkUserImageUploadStatusV2 = makeUploadStatusChecker<UserImageVOResponse>('posterimage/getUserImageStatusV2');
const checkUserVideoUploadStatusV2 = makeUploadStatusChecker<UserVideoVOResponse>('postervideo/getUserVideoStatusV2');
const checkUserAudioUploadStatusV2 = makeUploadStatusChecker<UserAudioVOResponse>('posteraudio/getUserAudioStatusV2');

interface UserDrawingUploadV2Data {
  url: string;
}

const checkUserDrawingUploadStatusV2 = makeUploadStatusChecker<UserDrawingUploadV2Data>('posterimage/getUserDrawingStatusV2');

/**
 * Single error-translation point for the image V2 upload's pollableJob transport. Also reused by
 * {@link runUserDrawingUploadJob}: the drawing V2 upload's FAIL codes (`invalid_image`,
 * `upload_failed`) are the same wire vocabulary this function already handles, resolving to the
 * same messages.
 */
const getUserImageUploadFailureMessage = (code: string): string => {
  if (code === UserImageUploadErrorCode.IMAGE_DIMENSIONS_EXCEEDED) {
    return window.i18next.t('pmwjs_image_upload_dimensions_exceeded_error');
  }
  if (code === UserImageUploadErrorCode.INVALID_IMAGE) {
    return window.i18next.t('pmwjs_image_upload_invalid_error');
  }
  return window.i18next.t('pmwjs_upload_error');
};

/**
 * Audio FAIL codes, and the video ones other than `upload_limits_exceeded` (`upload_failed`,
 * `upload_already_in_progress`), have no business-distinguishable message, so this resolves the
 * generic upload-error message regardless of the FAIL code.
 */
const getGenericUploadFailureMessage = (): string => {
  return window.i18next.t('pmwjs_upload_error');
};

interface MediaUploadJobConfig<T> {
  kickoff: () => Promise<PollKickoffAck>;
  status: UploadStatusChecker<T>;
  getFailureMessage: (code: string) => string;
  onAck?: (ack: PollKickoffAck) => void;
}

/**
 * A video refused after the upload was accepted — a stock import or a recording whose length only
 * the server could measure — for being too long or too large to process gets its own message;
 * every other video FAIL code gets the generic one.
 */
const getUserVideoUploadFailureMessage = (code: string): string => {
  if (code === VIDEO_UPLOAD_LIMITS_EXCEEDED_ERROR_CODE) {
    return window.i18next.t('pmwjs_video_upload_limits_exceeded_error');
  }
  return getGenericUploadFailureMessage();
};

/** Whether an upload failed because the server refused the video as too long or too large. */
const isVideoUploadLimitsExceededError = (error: unknown): boolean => {
  return getUploadFailureCode(error) === VIDEO_UPLOAD_LIMITS_EXCEEDED_ERROR_CODE;
};

/**
 * The server's code for a failed upload. A refusal of the upload request itself carries it on the error
 * (writeLocal copies the response code); a job that fails later carries it on the PollFailError cause
 * that runMediaUploadJob attaches.
 */
const getUploadFailureCode = (error: unknown): string | undefined => {
  if (!(error instanceof Error)) {
    return undefined;
  }
  if (error.cause instanceof PollFailError) {
    return error.cause.code;
  }
  return 'code' in error && typeof error.code === 'string' ? error.code : undefined;
};

/**
 * Shared run-and-translate transport for every media-upload pollableJob, mirroring
 * `utils/image.util.ts`'s bg-removal adapter. Warms the SSE fast path, runs the job, and funnels
 * every failure through one catch: kickoff rejections (e.g. an HTTP 413 from an oversized upload)
 * pass through untranslated so `uploadFiles`'s status-code special case keeps working, while
 * job-level failures (PollFailError/PollError/PollTimeoutError) translate to a plain Error so
 * every existing catch block's `e.message` growl behaves as it did on the legacy path. Each media
 * type differs only in its resolved payload type, status checker and FAIL-code translator.
 */
export const runMediaUploadJob = async <T>({kickoff, status, getFailureMessage, onAck}: MediaUploadJobConfig<T>): Promise<T> => {
  warmStreamFastPath(); // overlap the SSE handshake with the upload POST so awaitJob finds a warm connection
  try {
    return await pollableJob<T>({
      kickoff,
      status,
      timeoutMs: USER_MEDIA_UPLOAD_POLL_TIMEOUT_MS,
      onAck,
    });
  } catch (e) {
    if (e instanceof PollFailError) {
      throw new Error(getFailureMessage(e.code), {cause: e});
    }
    if (e instanceof PollError || e instanceof PollTimeoutError) {
      throw new Error(window.i18next.t('pmwjs_upload_error'), {cause: e});
    }
    throw e;
  }
};

export const runUserImageUploadJob = (kickoff: () => Promise<PollKickoffAck>): Promise<UserImageVOResponse> => {
  return runMediaUploadJob<UserImageVOResponse>({kickoff, status: checkUserImageUploadStatusV2, getFailureMessage: getUserImageUploadFailureMessage});
};

export const runUserVideoUploadJob = (kickoff: () => Promise<PollKickoffAck>): Promise<UserVideoVOResponse> => {
  return runMediaUploadJob<UserVideoVOResponse>({kickoff, status: checkUserVideoUploadStatusV2, getFailureMessage: getUserVideoUploadFailureMessage});
};

export const runUserAudioUploadJob = (kickoff: () => Promise<PollKickoffAck>): Promise<UserAudioVOResponse> => {
  return runMediaUploadJob<UserAudioVOResponse>({kickoff, status: checkUserAudioUploadStatusV2, getFailureMessage: getGenericUploadFailureMessage});
};

/**
 * Runs the drawing-upload job through {@link runMediaUploadJob} (reusing the image FAIL-code
 * translator, since drawing shares the `invalid_image`/`upload_failed` vocabulary) and unwraps the
 * V2 DONE payload's `{url}` wrapper to a bare string, matching what the legacy
 * `posterimage/getUserDrawingStatus` poller already resolved.
 */
export const runUserDrawingUploadJob = async (kickoff: () => Promise<PollKickoffAck>): Promise<string> => {
  const {url} = await runMediaUploadJob<UserDrawingUploadV2Data>({kickoff, status: checkUserDrawingUploadStatusV2, getFailureMessage: getUserImageUploadFailureMessage});
  return url;
};

/** Brand uploads go to their own endpoints, which link the stored image into a kit as they save it. */
const BRAND_UPLOAD_URI_FUNCTIONS: Partial<Record<string, string>> = {
  [MultiMediaUploadSource.BRAND_LOGOS]: 'brandLogoUpload',
  [MultiMediaUploadSource.BRAND_IMAGES]: 'brandImageUpload',
};

/**
 * The brand endpoints store images only. A kit's picker shows the Videos tab too, so a video uploaded
 * there takes the regular upload path and is linked into the kit by the picker once it is stored.
 */
const isStoredByBrandUploadEndpoint = (type: UserMediaType): boolean => {
  return type === UserMediaType.IMAGE || type === UserMediaType.IMAGE_BACKGROUND;
};

/**
 * Brand uploads file into the kit on screen. A picker that can show another kit (the uploads panel's kit
 * switcher) names it on the upload; every other brand-source picker is opened for the active kit.
 */
const appendBrandIdForBrandUpload = (fd: FormData, src: string): void => {
  if (BRAND_UPLOAD_URI_FUNCTIONS[src] === undefined || fd.has('brandId')) {
    return;
  }

  const activeBrandId = window.PMW.redux.store.getState().brands.activeBrandId;
  if (activeBrandId) {
    fd.append('brandId', activeBrandId);
  }
};

/**
 * Uploads the file to our server. Every media type/src combination whose resolved legacy uri has
 * a V2 counterpart (IMAGE, IMAGE_BACKGROUND, VIDEO, VIDEO_BACKGROUND, AUDIO, DRAWING) routes
 * through the V2 pollableJob transport; the legacy long-poll path remains as the fallback for any
 * combination without one.
 * @param {any} fd
 * @param {string} loadingKey loading key for handling progress bar
 * @param {string} type one of the MEDIA_TYPE_* constants
 * @param {string} src one of the MULTI_MEDIA_SOURCE_* constants
 * @return {Promise<*>}
 */
export const uploadFile = async (fd: FormData, loadingKey: string, type: UserMediaType, src: string): Promise<any> => {
  if (type === UserMediaType.DRAWING) {
    window.PMW.gtm.trackGA4CustomEvent(GA4EventName.END_DRAWING);
  }

  appendBrandIdForBrandUpload(fd, src);
  const v2Uri = getV2UploadUri(type, src);
  if (v2Uri) {
    const kickoff = (): Promise<PollKickoffAck> => getUploadFileAjaxFunctionV2(fd, loadingKey, v2Uri);
    if (type === UserMediaType.IMAGE || type === UserMediaType.IMAGE_BACKGROUND) {
      return runUserImageUploadJob(kickoff);
    }
    if (type === UserMediaType.VIDEO || type === UserMediaType.VIDEO_BACKGROUND) {
      return runUserVideoUploadJob(kickoff);
    }
    if (type === UserMediaType.AUDIO) {
      return runUserAudioUploadJob(kickoff);
    }
    if (type === UserMediaType.DRAWING) {
      return runUserDrawingUploadJob(kickoff);
    }
  }

  return (await window.PMW.pollingAjaxCallAsync(getUploadFileAjaxFunction.bind(null, fd, loadingKey, type, src), checkUploadStatus.bind(null, type))) as Promise<any>;
};

/** Uploads a canvas as a PNG into the user's uploads, as if they had picked the file from their device. */
export const uploadCanvasAsUserImage = async (canvas: HTMLCanvasElement, fileName: string): Promise<UserImageVOResponse> => {
  const fd = new FormData();
  fd.append('Filedata', new File([await canvasToPngBlob(canvas)], fileName, {type: PNG_MIME_TYPE}));
  fd.append('type', UserMediaType.IMAGE);
  fd.append('mirror', 'false');
  const uploadedImage = (await uploadFile(fd, UPLOAD_LOADING_KEY, UserMediaType.IMAGE, MULTI_MEDIA_SOURCE_UPLOAD)) as UserImageVOResponse;
  updateUnlinkedMediaStateForGuest(UserMediaType.IMAGE, uploadedImage);
  return uploadedImage;
};

const getDeferredImageUploadData = async (uploadData: UploadingFilesData): Promise<DeferredImageUploadData> => {
  const {width, height} = await getClampedImageFileDimensions(uploadData.file);

  return {
    type: ElementDataType.IMAGE,
    isDeferredEditorUpload: true,
    file: uploadData.file,
    width,
    height,
  };
};

const onFileUploaded = async (data: UserMediaVOResponse, uploadData: UploadingFilesData): Promise<void> => {
  await moveItemToCollection(uploadData.type, data.filename, uploadData.collectionId);
  Emitter.emit('userMedia:upload:success', {type: uploadData.type, file: uploadData.file, collectionId: uploadData.collectionId, data});
};

export const checkUploadStatus = (type: UserMediaType, itemHashedId: string): any => {
  return window.PMW.readLocal(getCheckUploadStatusUriForMediaType(type), {
    hashedId: itemHashedId,
  });
};

const UPLOAD_AJAX_TIMEOUT_MS = 7200000;

interface UploadAjaxOptions {
  timeout: number;
  xhr: () => XMLHttpRequest;
  cache: boolean;
  processData: boolean;
  contentType: boolean;
}

/** Shared jQuery ajax opts for the file-upload FormData posts (legacy + V2 kickoff alike); keeps the progress bar wiring in one place. */
const getUploadAjaxOptions = (loadingKey: string): UploadAjaxOptions => {
  return {
    timeout: UPLOAD_AJAX_TIMEOUT_MS,
    xhr: progressHandler.bind(null, loadingKey),
    cache: false,
    processData: false, // Don't process the files
    contentType: false, // Set content type to false as jQuery will tell the server its a query string request
  };
};

const getUploadFileAjaxFunction = (fd: FormData, loadingKey: string, type: UserMediaType, src: string) => {
  return window.PMW.writeLocal(getUploadUriForMediaType(type, src), fd, 'json', getUploadAjaxOptions(loadingKey));
};

/**
 * V2 uris for every media type/src combination that has a pollableJob kickoff (image uploads from
 * wave A; video, audio and drawing added here). Keyed off the legacy resolved uri so the routing
 * decision in {@link uploadFile} is provably equivalent to the pre-migration behavior; any
 * resolved uri without an entry here falls back to the legacy long-poll transport.
 */
const UPLOAD_V2_URI_BY_LEGACY_URI: Record<string, string> = {
  'posterimage/upload': 'posterimage/uploadV2',
  'posterimage/emailUpload': 'posterimage/emailUploadV2',
  'postervideo/upload': 'postervideo/uploadV2',
  'postervideo/emailUpload': 'postervideo/emailUploadV2',
  'posteraudio/upload': 'posteraudio/uploadV2',
  'posterimage/uploadDrawingImage': 'posterimage/uploadDrawingImageV2',
  'posterimage/brandLogoUpload': 'posterimage/brandLogoUploadV2',
  'posterimage/brandImageUpload': 'posterimage/brandImageUploadV2',
};

const getV2UploadUri = (type: UserMediaType, src: string): string | null => {
  return UPLOAD_V2_URI_BY_LEGACY_URI[getUploadUriForMediaType(type, src)] ?? null;
};

export const getUploadFileAjaxFunctionV2 = async (fd: FormData, loadingKey: string, uri: string): Promise<PollKickoffAck> => {
  return (await window.PMW.writeLocal(uri, fd, 'json', getUploadAjaxOptions(loadingKey))) as PollKickoffAck;
};

/**
 * Returns server side controller function for type of uploaded file
 * @param {string} type one of the MEDIA_TYPE_* constants
 * @param {string} src one of the MULTI_MEDIA_SOURCE_* constants
 * @return {string}
 */
export const getUploadUriForMediaType = (type: UserMediaType, src: string): string => {
  const brandUploadUriFunction = BRAND_UPLOAD_URI_FUNCTIONS[src];
  if (brandUploadUriFunction !== undefined && isStoredByBrandUploadEndpoint(type)) {
    return `posterimage/${brandUploadUriFunction}`;
  }

  let uriFunction;

  switch (src) {
    case MULTI_MEDIA_SOURCE_EMAIL_UPLOAD:
      uriFunction = 'emailUpload';
      break;

    case MULTI_MEDIA_SOURCE_DRAWING:
      uriFunction = 'uploadDrawingImage';
      break;

    case MULTI_MEDIA_SOURCE_UPLOAD:
    default:
      uriFunction = 'upload';
  }

  switch (type) {
    case UserMediaType.IMAGE:
    case UserMediaType.IMAGE_BACKGROUND:
    case UserMediaType.DRAWING:
      return `posterimage/${uriFunction}`;
    case UserMediaType.VIDEO:
    case UserMediaType.VIDEO_BACKGROUND:
      return `postervideo/${uriFunction}`;
    case UserMediaType.AUDIO:
      return `posteraudio/${uriFunction}`;
    default:
      throw new Error(`Unhandled media type: ${type}`);
  }
};
/**
 * Returns server side controller function for type of uploaded file
 * @param {string} type one of the MEDIA_TYPE_* constants
 * @return {string}
 */
const getCheckUploadStatusUriForMediaType = (type: UserMediaType): string => {
  switch (type) {
    case UserMediaType.VIDEO:
    case UserMediaType.VIDEO_BACKGROUND:
      return 'postervideo/getUserVideoStatus';
    case UserMediaType.AUDIO:
      return 'posteraudio/getUserAudioStatus';
    case UserMediaType.DRAWING:
      return 'posterimage/getUserDrawingStatus';
    case UserMediaType.IMAGE_BACKGROUND:
    case UserMediaType.IMAGE:
      return 'posterimage/getUserImageStatus';

    default:
      throw new Error(`Unhandled media type: ${type}`);
  }
};

const getInvalidExtensionsFromInvalidFiles = (invalidFiles: File[]): string[] => {
  const invalidExtensions: string[] = [];

  invalidFiles.forEach((invalidFile) => {
    const extension = invalidFile.name.split('.').pop();

    if (extension && !invalidExtensions.includes(`.${extension}`)) {
      invalidExtensions.push(`.${extension}`);
    }
  });

  return invalidExtensions;
};

export const getMaxAllowedUploadFileSize = (): number => {
  return MAX_ALLOWED_FILE_SIZE;
};

export const getReadableMaxAllowedUploadFileSize = (): string => {
  const GB = 1024 * 1024 * 1024;
  const MB = 1024 * 1024;
  const KB = 1024;

  if (MAX_ALLOWED_FILE_SIZE >= GB) return `${(MAX_ALLOWED_FILE_SIZE / GB).toFixed(1)} GB`;
  if (MAX_ALLOWED_FILE_SIZE >= MB) return `${(MAX_ALLOWED_FILE_SIZE / MB).toFixed(1)} MB`;
  if (MAX_ALLOWED_FILE_SIZE >= KB) return `${(MAX_ALLOWED_FILE_SIZE / KB).toFixed(1)} KB`;
  return `${MAX_ALLOWED_FILE_SIZE} bytes`;
};

const showErrorMessageForInvalidOrSizeExceededFiles = (invalidFiles: File[], filesExceedingUploadLimit: File[]): void => {
  if (invalidFiles.length && filesExceedingUploadLimit.length) {
    showMessageForInvalidAndSizeExceededFiles(invalidFiles, filesExceedingUploadLimit);
  } else if (invalidFiles.length) {
    showMessageForInvalidFiles(invalidFiles);
  } else if (filesExceedingUploadLimit.length) {
    showMessageForSizeExceededFiles(filesExceedingUploadLimit);
  }
};

const showMessageForInvalidAndSizeExceededFiles = (invalidFiles: File[], filesExceedingUploadLimit: File[]): void => {
  const invalidExtensions = getInvalidExtensionsFromInvalidFiles(invalidFiles);

  if (invalidExtensions.length === 1) {
    showMessageGrowl({
      type: GROWL_TYPE.DANGER,
      text: window.i18next.t('pmwjs_upload_some_invalid_media_file_and_exceeding_limit', {
        extension: invalidExtensions,
        numfiles: invalidFiles.length + filesExceedingUploadLimit.length,
        uploadFileSizeLimit: getReadableMaxAllowedUploadFileSize(),
      }),
    });
  } else {
    const last = invalidExtensions.pop();
    showMessageGrowl({
      type: GROWL_TYPE.DANGER,
      text: window.i18next.t('pmwjs_upload_some_invalid_media_files_and_exceeding_limit', {
        extensions: `${invalidExtensions.join(', ')} & ${last ?? ''}`,
        numfiles: invalidFiles.length + filesExceedingUploadLimit.length,
        uploadFileSizeLimit: getReadableMaxAllowedUploadFileSize(),
      }),
    });
  }
};

const showMessageForSizeExceededFiles = (filesExceedingUploadLimit: File[]): void => {
  if (filesExceedingUploadLimit.length === 1) {
    showMessageGrowl({
      type: GROWL_TYPE.DANGER,
      text: window.i18next.t('pmwjs_upload_some_media_file_exceeding_limit', {
        uploadFileSizeLimit: getReadableMaxAllowedUploadFileSize(),
      }),
    });
  } else {
    showMessageGrowl({
      type: GROWL_TYPE.DANGER,
      text: window.i18next.t('pmwjs_upload_some_media_files_exceeding_limit', {
        uploadFileSizeLimit: getReadableMaxAllowedUploadFileSize(),
        numfiles: filesExceedingUploadLimit.length,
      }),
    });
  }
};

const showMessageForInvalidFiles = (invalidFiles: File[]): void => {
  const invalidExtensions = getInvalidExtensionsFromInvalidFiles(invalidFiles);

  if (invalidFiles.length === 1) {
    showMessageGrowl({
      type: GROWL_TYPE.DANGER,
      text: window.i18next.t('pmwjs_upload_some_invalid_media_file', {
        extension: invalidExtensions,
      }),
    });
  } else {
    const last = invalidExtensions.pop();
    showMessageGrowl({
      type: GROWL_TYPE.DANGER,
      text: window.i18next.t('pmwjs_upload_some_invalid_media_files', {
        numfiles: invalidFiles.length,
        extensions: `${invalidExtensions.join(', ')} & ${last ?? ''}`,
      }),
    });
  }
};

const getTextForMyMultiMedia = (multiMediaType: UserMediaType): string => {
  switch (multiMediaType) {
    case UserMediaType.IMAGE_BACKGROUND:
      return window.i18next.t('pmwjs_my_backgrounds');

    case UserMediaType.VIDEO:
      return window.i18next.t('pmwjs_my_videos');

    case UserMediaType.AUDIO:
      return window.i18next.t('pmwjs_my_audios');

    case UserMediaType.DRAWING:
      return window.i18next.t('pmwjs_my_drawings');

    case UserMediaType.IMAGE:
    default:
      return window.i18next.t('pmwjs_my_photos');
  }
};

export type GridUserMediaItemStorage = GridUserImageItemStorage | GridUserVideoItemStorage | GridUserAudioItemStorage | GridStockImageItemStorage | GridAiImageItemStorage;
export type UserMediaVOResponse = UserImageVOResponse | UserVideoVOResponse | UserAudioVOResponse | AIGenerateImageBackendVO;
export type MediaData = ImageData | VideoData | AudioData;

export const getMediaStorageForbackendData = (itemData: UserMediaVOResponse, type: UserMediaType): GridUserMediaItemStorage => {
  switch (type) {
    case UserMediaType.AUDIO:
      return getGridUserAudioItemStorageForUserAudioVO(itemData as UserAudioVOResponse);
    case UserMediaType.VIDEO:
    case UserMediaType.VIDEO_BACKGROUND:
      return getGridUserVideoItemStorageForUserVideoVO(itemData as UserVideoVOResponse);
    case UserMediaType.IMAGE:
    case UserMediaType.IMAGE_BACKGROUND:
      return getGridUserImageItemStorageForUserImageVO(itemData as UserImageVOResponse);
    default:
      throw new Error(`Invalid media type: ${type}`);
  }
};

export const isBrandKitLogoGridItem = (gridItem: GridItemStorage): boolean => {
  return isGridUserImageItem(gridItem) && (gridItem.brandKitName !== undefined || gridItem.source === UserImageSource.BRAND_LOGOS);
};

export const gridUserMediaItemStorageToMediaData = (gridItem: GridItemStorage): MediaData => {
  switch (gridItem.type) {
    case GRID_ITEM_TYPE.USER_IMAGE:
      return getImageDataFromGridUserImageItemStorage(gridItem as GridUserImageItemStorage);
    case GRID_ITEM_TYPE.USER_VIDEO:
      return getVideoDataFromGridUserVideoItemStorage(gridItem as GridUserVideoItemStorage);
    case GRID_ITEM_TYPE.USER_AUDIO:
      return getAudioDataFromGridUserAudioItemStorage(gridItem as GridUserAudioItemStorage);
    default:
      throw new Error(`Invalid media type: ${gridItem.type}`);
  }
};

export const linkMediaItems = async (): Promise<void> => {
  const unlinkedImages: string[] = [];
  const unlinkedVideos: string[] = [];
  const unlinkedAudios: string[] = [];

  window.PMW.redux.store.getState().mediaUploads.unlinkedMedia.forEach((item) => {
    if (item.type === GRID_ITEM_TYPE.USER_IMAGE) {
      unlinkedImages.push(item.id);
    }
    if (item.type === GRID_ITEM_TYPE.USER_VIDEO) {
      unlinkedVideos.push(item.id);
    }
    if (item.type === GRID_ITEM_TYPE.USER_AUDIO) {
      unlinkedAudios.push(item.id);
    }
  });

  if (unlinkedImages.length) {
    try {
      await window.PMW.writeLocal('posterimage/linkUserImages', {
        uids: unlinkedImages,
        userId: getUserId(),
      });
    } catch (e) {
      console.error(e);
      openModal(MODAL_IDS.ERROR_MODAL_ID, {
        message: window.i18next.t('pmwjs_general_ajax_error'),
      });
    }
  }
  if (unlinkedVideos.length) {
    try {
      await window.PMW.writeLocal('postervideo/linkUserVideos', {
        uids: unlinkedVideos,
        userId: getUserId(),
      });
    } catch (e) {
      console.error(e);
      openModal(MODAL_IDS.ERROR_MODAL_ID, {
        message: window.i18next.t('pmwjs_general_ajax_error'),
      });
    }
  }
  if (unlinkedAudios.length) {
    try {
      await window.PMW.writeLocal('posteraudio/linkIds', {
        uids: unlinkedAudios,
        userId: getUserId(),
      });
    } catch (e) {
      console.error(e);
      openModal(MODAL_IDS.ERROR_MODAL_ID, {
        message: window.i18next.t('pmwjs_general_ajax_error'),
      });
    }
  }
  window.PMW.redux.store.dispatch(updateUnlinkedMediaState([]));
};

export const updateUnlinkedMediaStateForGuest = (type: UserMediaType, items: UserMediaVOResponse | UserMediaVOResponse[]): void => {
  if (isUserLoggedIn()) {
    return;
  }

  const mediaItems = (Array.isArray(items) ? items : [items]).map((item) => getMediaStorageForbackendData(item, type));
  window.PMW.redux.store.dispatch(updateUnlinkedMediaState(mediaItems));
};

export const userMediaVOToElementData = (type: UserMediaType, data: UserMediaVOResponse): ElementData => {
  switch (type) {
    case UserMediaType.AUDIO:
      const audioData = data as UserAudioVOResponse;
      return {
        filename: audioData.filename,
        extension: audioData.ext,
        duration: audioData.duration,
        src: audioData.src,
        name: audioData.name,
        type: ElementDataType.AUDIO,
      };
    case UserMediaType.VIDEO:
      const videoData = data as UserVideoVOResponse;
      return {
        hashedFilename: videoData.filename,
        extension: videoData.ext,
        source: videoData.src,
        width: videoData.width,
        height: videoData.height,
        duration: videoData.duration,
        frameRate: videoData.frameRate,
        hasTransparency: videoData.hasTransparency,
        type: ElementDataType.VIDEO,
      };
    case UserMediaType.IMAGE:
      const imageData = data as UserImageVOResponse;
      return {
        hashedFilename: imageData.filename,
        extension: imageData.ext,
        hasTransparency: imageData.hasTransparency,
        uploaderId: Number(imageData.uploaderId),
        width: imageData.width,
        source: imageData.src,
        height: imageData.height,
        type: ElementDataType.IMAGE,
      };
    default:
      throw new Error('cannot convert this type to user media vo response');
  }
};

const getMessageForInvalidMediaMove = (mediaType: UserMediaType): string => {
  switch (mediaType) {
    case UserMediaType.VIDEO:
      return window.i18next.t('pmwjs_can_not_move_video');
    case UserMediaType.AUDIO:
      return window.i18next.t('pmwjs_can_not_move_audio');
    default:
      return window.i18next.t('pmwjs_can_not_move_image');
  }
};

const getMessageForInvalidMediaRemoval = (mediaType: UserMediaType): string => {
  switch (mediaType) {
    case UserMediaType.VIDEO:
      return window.i18next.t('pmwjs_can_not_remove_video');
    case UserMediaType.AUDIO:
      return window.i18next.t('pmwjs_can_not_remove_audio');
    default:
      return window.i18next.t('pmwjs_can_not_remove_image');
  }
};

const getMessageForFailedMediaRemoval = (mediaType: UserMediaType): string => {
  switch (mediaType) {
    case UserMediaType.VIDEO:
      return window.i18next.t('pmwjs_failed_move_video');
    case UserMediaType.AUDIO:
      return window.i18next.t('pmwjs_failed_move_audio');
    default:
      return window.i18next.t('pmwjs_failed_move_image');
  }
};

export const removeUserMediaItems = async (userID: number, gridItems: GridUserMediaItemStorage[], mediaType: UserMediaType, currentCollectionId: string): Promise<string[]> => {
  const filteredItems: string[] = [];
  const filteredItemIds: string[] = [];
  gridItems.forEach((item) => {
    const isNotOwner = currentCollectionId && userID !== item.uploaderId;
    if (!isNotOwner) {
      filteredItems.push(item.hashedFilename);
      filteredItemIds.push(item.id);
    }
  });
  if (filteredItems.length !== gridItems.length) {
    showMessageGrowl({text: getMessageForInvalidMediaRemoval(mediaType), type: GROWL_TYPE.DANGER});
  }
  if (!filteredItems.length) {
    return [];
  }
  try {
    await removeUserItems(filteredItems, mediaType);
  } catch (e) {
    showMessageGrowl({text: getMessageForFailedMediaRemoval(mediaType), type: GROWL_TYPE.DANGER});
    return [];
  }
  return filteredItemIds;
};
